import { Field } from "@base-ui/react/field";
import { Input as BaseInput } from "@base-ui/react/input";
import { cn } from "@marketing-sites/utils/cn";
import type { ValidationRule } from "@marketing-sites/utils/validation";
import { composeValidate } from "@marketing-sites/utils/validation";
import {
  FieldErrors,
  type FieldErrorsProps,
} from "../form/validation/field-errors";

export type InputProps = BaseInput.Props & {
  label?: string;
  fieldProps?: Omit<Field.Root.Props, "name" | "validate">;
  validations?: ValidationRule[];
  errorProps?: FieldErrorsProps["errorProps"];
  requiredMessage?: string;
  typeMismatchMessage?: string;
};

/**
 * A labelled text input with built-in error display.
 *
 * Must be rendered inside a Base UI `<Form>` for validation and error
 * messages to work. Without it, `Field.Error` will never appear because
 * the validation lifecycle (`field.validate()` on submit) is not triggered.
 *
 * Pass `fieldProps` to configure the `Field.Root` (e.g. `validate`, `validationMode`),
 * `validations` to define custom validation rules, and `errorProps` to customise
 * the `Field.Error` elements (e.g. `className`, `style`).
 *
 * @see https://base-ui.com/react/components/form
 * @see https://base-ui.com/react/components/field
 */
export function Input({
  name,
  label,
  className,
  fieldProps,
  validations,
  errorProps,
  requiredMessage,
  typeMismatchMessage,
  ...rest
}: InputProps) {
  return (
    <Field.Root
      name={name}
      validate={composeValidate(validations, rest.required, requiredMessage)}
      {...fieldProps}
    >
      {label && (
        <Field.Label className="label">
          {label} {rest.required && <span>*</span>}
        </Field.Label>
      )}
      <BaseInput name={name} className={cn("input", className)} {...rest} />
      <FieldErrors
        errorProps={errorProps}
        requiredMessage={requiredMessage}
        typeMismatchMessage={typeMismatchMessage}
      />
    </Field.Root>
  );
}
